import { Minus } from "lucide-react";

import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils/workflow-ids";
import { ChatWorkflowRequestBodyDto } from "@intelligems/ig-types/dtos/ai/workflows/chat-workflow";
import { ExecutionPanel } from "@src/features/ai/components/WorkflowEditor/execution/ExecutionPanel";
import { ExecutionPanelChat } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelChat";
import { useDispatch, useSelector } from "@src/store";
import { ExperienceBuilderSelector } from "@src/store/slices/experienceBuilder";
import {
  ExperienceBuilderAiAction,
  ExperienceBuilderAiSelector,
} from "@src/store/slices/experienceBuilder/ai";

import { NavigationChoiceSheet } from "./NavigationChoiceSheet";
import { transformVivMessage } from "./transformVivMessage";
import { VivEmptyState } from "./VivEmptyState";
import { VivMenuItems } from "./VivMenuItems";
import { OnAiChatSend } from "@src/features/ai/components/WorkflowEditor/execution/types";

interface VivChatProps {
  onClose?: () => void;
}

export const VivChat = ({ onClose }: VivChatProps) => {
  const dispatch = useDispatch();
  // Busy = a turn is in flight OR a continuation sequence is mid-flow between
  // turns. Both are owned by Redux now (the `fireVivTurn` thunk sets the
  // pending flag; the side-effect listener owns `continuationInFlight`), so the
  // component just reads the derived flag.
  const busy = useSelector(ExperienceBuilderAiSelector.getIsAiBusy);
  const pageState = useSelector(ExperienceBuilderSelector.getPageState);
  // Block AI turns whenever the iframe is anything other than `ready` —
  // capturing during loading or an error state would either hang on the
  // capture gate or ship un-modded HTML. The PageState overlay tells the
  // user what's wrong; the chat just waits its turn.
  const isPageReady = pageState?.type === "ready";
  // Onsite-upsell chats get their own composer placeholder (paired with the
  // upsell chips); other experiences keep the generic prompt.
  const isOnsiteUpsell = useSelector(
    ExperienceBuilderSelector.isOnsiteUpsellBuilder
  );

  const onSend: OnAiChatSend = {
    chat: (payload: ChatWorkflowRequestBodyDto) => {
      if (!payload.input) {
        return;
      }
      void dispatch(
        ExperienceBuilderAiAction.fireVivTurn({
          kind: "user-first",
          input: payload.input,
        })
      );
    },
    stop: () =>
      void dispatch(ExperienceBuilderAiAction.stopVivChat("user_stop")),
    isPending: busy,
    // Disable (but don't show "busy" UI) whenever the iframe isn't in
    // a ready state — capturing during loading or an error state would
    // either hang on the capture gate or ship un-modded HTML. The
    // PageState overlay tells the user what's wrong.
    disabled: !isPageReady,
  };

  return (
    <ExecutionPanel
      sx={{ position: "relative", px: 3 }}
      feature="modifications_chat"
      onClose={onClose}
      closeIcon={<Minus size={20} />}
      onSend={onSend}
      placeholder={
        isOnsiteUpsell ? "Or describe what you'd like to build..." : undefined
      }
      workflowId={WORKFLOW_IDS.MODIFICATIONS_CHAT}
      showSystemMessages={false}
      showToolCallOutput={false}
      clearReasoningOnMessage={true}
      transformMessage={transformVivMessage}
      hiddenTools={[
        "handoffToExperienceCreation",
        "visual_builder_update_mods",
        "visual_builder_locate",
        "visual_builder_evaluate_js",
        "visual_builder_screenshot",
        "visual_builder_knowledge",
        "visual_builder_navigate",
        "visual_builder_set_device",
        "visual_builder_set_variation",
        "visual_builder_list_products",
        "visual_builder_get_product",
        "visual_builder_list_collections",
        "visual_builder_get_collection",
        "visual_builder_list_pages",
        "visual_builder_list_files",
        "visual_builder_get_file",
        "visual_builder_search_destinations",
        // Agentic Logic reads Viv makes while choosing or building an upsell's
        // rule. `create_agentic_logic_rule` stays visible: the merchant approved
        // it a turn earlier and should see it happen.
        "list_agentic_logic_rules",
        "get_agentic_logic_rule",
        "list_agentic_logic_templates",
        "validate_agentic_logic_template",
      ]}
      slots={{
        // Keeps the library reachable once the conversation has turns and the
        // empty state's link is gone.
        menuItems: <VivMenuItems />,
        // Idea Library empty state: featured prompt chips shown while the
        // conversation has no turns yet.
        conversationEmpty: <VivEmptyState />,
        // Own the body so the navigation choice sheet can slide up over the
        // conversation. ExecutionPanelChat is the unchanged default body; the
        // sheet absolutely-positions against the relative panel body.
        body: (
          <>
            <ExecutionPanelChat />
            <NavigationChoiceSheet />
          </>
        ),
      }}
    />
  );
};
